import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { IPaginationPayload } from '../../../../interfaces/shared/IPaginationPayload';
import { ApiResponse } from '../../../../interfaces/models/models/paginated-response.model';

import { AxiosError } from 'axios';
import { ISampleListState } from '../../../../interfaces/sample/ISampleList';
import { PaginatedSample } from '../../../../interfaces/sample/paginatedStore.type';
import { SamplesService } from './samplesApi';
import { ISample } from '../../../../interfaces/sample/ISample';
import { ISampleSchema } from '../../../../interfaces/sample/ISampleSchema';

const initialState: ISampleListState = {
    samplesAreLoading: false,
    toastSuccessRequest: false,
    sampleIsLoading: false,
    requestStatus: 'default',
    message: '',
};

export const tryToFetchSamples = createAsyncThunk<
    PaginatedSample,
    IPaginationPayload
>('samples/tryToFetchSamples', async ({ pageNumber, pageSize, filters }) => {
    const result = await SamplesService.tryToFetchSamples(
        pageNumber,
        pageSize,
        filters,
    );
    return result?.data;
});

export const tryToFetchSingleSample = createAsyncThunk<ISample, string>(
    'samples/tryToFetchSingleSample',
    async (id: string) => {
        const result = await SamplesService.tryToFetchSingleSample(id);
        return result?.data;
    },
);

export const tryToEditSample = createAsyncThunk<
    ApiResponse<ISampleSchema>,
    ISample
>('samples/tryToEditSample', async (data: any, { rejectWithValue }) => {
    try {
        const result = await SamplesService.tryToEditSample(
            data._id || '',
            data?.data,
        );
        return result.data;
    } catch (error) {
        const err: any = error as AxiosError;
        return rejectWithValue(err?.response?.data?.message);
    }
});

export const tryToAddSample = createAsyncThunk<
    ApiResponse<ISampleSchema>,
    ISample
>('samples/tryToAddSample', async (data: any, { rejectWithValue }) => {
    try {
        const result = await SamplesService.tryToAddSample(data);
        return result.data;
    } catch (error) {
        const err: any = error as AxiosError;
        return rejectWithValue(err?.response?.data?.message);
    }
});


export const samplesSlice = createSlice({
    name: 'samples',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // tryToFetchSamples
            .addCase(tryToFetchSamples.pending, (state) => {
                state.samplesAreLoading = true;
            })
            .addCase(tryToFetchSamples.rejected, (state) => {
                state.samplesAreLoading = false;
            })
            .addCase(tryToFetchSamples.fulfilled, (state, action) => {
                state.samplesAreLoading = false;
                state.samples = action.payload;
            })

            // tryToAddSample
            .addCase(tryToAddSample.pending, (state) => {
                state.sampleIsLoading = true;
            })
            .addCase(tryToAddSample.rejected, (state) => {
                state.sampleIsLoading = false;
            })
            .addCase(tryToAddSample.fulfilled, (state, action) => {
                state.sampleIsLoading = false;
                state.requestStatus = 'success';
                const data = action.payload.data || action.meta.arg;
                if (state.samples?.data)
                    state.samples.data.elements = [
                        ...state.samples?.data.elements,
                        data,
                    ];
            })

            // tryToEditSample
            .addCase(tryToEditSample.pending, (state) => {
                state.sampleIsLoading = true;
            })
            .addCase(tryToEditSample.rejected, (state) => {
                state.sampleIsLoading = false;
            })
            .addCase(tryToEditSample.fulfilled, (state, action) => {
                state.sampleIsLoading = false;
                if (state.samples?.data.elements)
                    state.samples.data.elements =
                        { ...state }.samples?.data.elements.map((sample) => {
                            if (sample?._id === action.payload?.data?._id) {
                                return {
                                    ...action.payload.data,
                                };
                            } else {
                                return sample;
                            }
                        }) || [];
                state.sampleDetails = action.payload.data;
            })

            // tryToFetchSingleSample
            .addCase(tryToFetchSingleSample.pending, (state) => {
                state.sampleIsLoading = true;
            })
            .addCase(tryToFetchSingleSample.rejected, (state) => {
                state.sampleIsLoading = false;
            })
            .addCase(tryToFetchSingleSample.fulfilled, (state, action) => {
                state.sampleIsLoading = false;
                state.sampleDetails = action.payload;
            });
    },
});

export default samplesSlice.reducer;
